import { Heading, ScrollArea } from '@mm/components';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useLandmineOpensCRUDActions } from '@mm/state/actions/LandmineOpensCRUDActions';
import { getPlanInfoFromBillingType } from '@mm/state/helpers/billing-info-helpers';
import {
  BasicPage,
  EnhancedBillingPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import {
  OpenBillingModalParams,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  getPageBillingType,
  getPlan,
  selectPlans,
  selectPlansMapping,
} from '@mm/state/selectors/facebookPageSelector';
import { defaultProPlanSelector } from '@mm/state/selectors/subscriptionSelectors';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { partial } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Checkbox from '../../../common/components/Checkbox';
import { PrimaryToolTip } from '../PrimaryToolTip/PrimaryToolTip';
import { PlanBadge } from '../ProBadge';
import Spinner from '../Spinner';
import './FacebookPagesAccess.pcss';

type Props = {
  children?: React.ReactNode;
  pages?: BasicPage[];
  selectedPages: BasicPage[];
  onPagesSelected: (pages: BasicPage[]) => void;
  disabled: boolean;
};

// tslint:disable-next-line: cyclomatic-complexity
const FacebookPageEntry = ({
  page,
  disabled,
  selected,
  onSelect,
  onAddSeatsClick,
  onUpgradeButtonClick,
  canSelectInformer,
}: {
  page: BasicPage;
  selected?: boolean;
  disabled?: boolean;
  onSelect: (page: BasicPage) => void;
  onAddSeatsClick: (page: BasicPage, plan?: EnhancedPlan) => void;
  onUpgradeButtonClick: (page: BasicPage, plan?: EnhancedPlan) => void;
  canSelectInformer: (id: number, canSelect: boolean) => void;
}) => {
  const { userSeats, userSeatsUsed } = page.subscription || {};
  const leftSeats = (userSeats || 0) - (userSeatsUsed || 0);
  const mappings = useSelector(selectPlansMapping);
  const plans = useSelector(selectPlans) || [];
  const plan = getPlan(page, plans);

  const pageBillingType = getPageBillingType(page, mappings, plan);
  const info = getPlanInfoFromBillingType(pageBillingType, page);

  const planWithPageBillingType = plan
    ? {
        ...plan,
        pageBillingType,
      }
    : plan;

  const isFreeTrial = page?.redemption?.freeTrial;

  const isFreeTrialPromotionWithoutSubscription =
    !!isFreeTrial && !page?.subscription;

  const shouldUpgrade =
    info.isFreePlan || isFreeTrialPromotionWithoutSubscription;

  const unlimitedSeats =
    (info.isPromoPlan && !isFreeTrial) ||
    info.isLegacyProPlan ||
    info.isLegacyTeamPlan ||
    (info.isCustomTeam && page.subscription?.userSeats === null);

  const hasFreeSeats = userSeats && leftSeats > 0;

  const shouldAddSeats =
    !unlimitedSeats &&
    !shouldUpgrade &&
    !hasFreeSeats &&
    pageBillingType !== PageBillingType.WP;

  const isDebtor = userSeats && leftSeats < 0;

  let content: string | JSX.Element = '';

  if (!hasFreeSeats) {
    content = 'No seats left';
  }

  if (shouldUpgrade) {
    content = '0 seats';
  }

  if (isDebtor) {
    content = (
      <PrimaryToolTip
        anchorElement={<div>{leftSeats} seats</div>}
        useHover={true}
        type="error"
        classNamePopoverContainer="facebook-pages-access__popover"
      >
        You have some legacy users on this account that causes it to exceed the
        seat limit on your subscription. In order to add another user please
        upgrade your subscription, buy additional seats, OR delete existing
        users to make room.
      </PrimaryToolTip>
    );
  }

  const canSelectPage = Boolean(!disabled && (unlimitedSeats || hasFreeSeats));

  useEffect(() => {
    canSelectInformer(page.id, canSelectPage);
  }, [page.id, canSelectPage]);

  return (
    <div className="facebook-pages-access__page">
      <Checkbox
        label={
          <Heading size={6} isBold={false}>
            {page.name || 'N/A'}
          </Heading>
        }
        className="facebook-pages-access__checkbox"
        isChecked={!!selected}
        disabled={!canSelectPage}
        onClick={!canSelectPage ? undefined : partial(onSelect, page)}
      />
      <PlanBadge
        className="facebook-pages-access__badge"
        billingType={pageBillingType}
      />
      <div className="facebook-pages-access__actions">
        {shouldAddSeats && (
          <span
            className="facebook-pages-access__action"
            onClick={partial(onAddSeatsClick, page, planWithPageBillingType)}
          >
            + Add seats
          </span>
        )}
        {shouldUpgrade && (
          <span
            className="facebook-pages-access__action"
            onClick={partial(
              onUpgradeButtonClick,
              page,
              planWithPageBillingType,
            )}
          >
            Upgrade
          </span>
        )}
        {!unlimitedSeats && (
          <span
            className={classNames('facebook-pages-access__status', {
              'facebook-pages-access__status--warn':
                !leftSeats || shouldUpgrade || isDebtor,
            })}
          >
            {content}
          </span>
        )}
      </div>
    </div>
  );
};

export const FacebookPagesAccess = (props: Props) => {
  const { disabled, onPagesSelected, selectedPages, pages } = props;

  const selectableIdsRef = useRef(new Set());

  const canSelectInformer = (id, canSelect) => {
    if (canSelect) {
      return selectableIdsRef.current.add(id);
    }
    return selectableIdsRef.current.delete(id);
  };

  const selectAll = (enable: boolean) => {
    if (!disabled) {
      const up =
        enable && pages
          ? [...pages?.filter(page => selectableIdsRef.current.has(page.id))]
          : [];
      onPagesSelected(up);
    }
  };

  const onSelect = (page: BasicPage) => {
    const match = selectedPages.find(v => v.id === page.id);

    if (!match) {
      return onPagesSelected([...selectedPages, page]);
    }

    const up = selectedPages.filter(v => v.id !== page.id);
    return onPagesSelected(up);
  };

  const modalActions = useModalActions();
  const defaultProPlan = useSelector(defaultProPlanSelector);
  const landmineOpensCRUDActions = useLandmineOpensCRUDActions();
  const appNavigationHistory = useSelector(
    (state: StoreState) => state.view.appNavigationHistory,
  );

  const openBillingModal = async (
    page: BasicPage,
    plan?: EnhancedPlan,
    seatsObject?: OpenBillingModalParams['seatsObject'],
  ) => {
    const planToUse = plan || defaultProPlan;

    if (planToUse) {
      const res = await landmineOpensCRUDActions.create({
        landmineName: 'INVITE_USER_MODAL',
        navigationHistory: appNavigationHistory,
        planName: 'pro',
        billingOpened: false,
      });

      modalActions.openBillingModal({
        facebookPage: page as EnhancedBillingPage,
        plan: planToUse,
        seatsObject,
        landmineOpenId: res?.id,
        stayOnPage: true,
        subscription: page.subscription,
        onSuccessBilling: () => {
          selectableIdsRef.current.clear();
        },
      });
    }
  };

  const onAddSeats = (page: BasicPage, plan?: EnhancedPlan) => {
    const seatsObject = {
      userSeats: page.subscription?.userSeats || 0,
      extraUserSeats: page.subscription?.extraUserSeats || 0,
    };

    openBillingModal(page, plan, seatsObject);
  };

  const onUpgrade = (page: BasicPage, plan?: EnhancedPlan) =>
    openBillingModal(page, plan);

  const isSelected = id =>
    !!selectedPages.find(selectedPage => selectedPage.id === id);

  const [isReady, setIsReady] = useState(false);

  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const companyId = useSelector(selectActiveCompanyId);

  useEffect(() => {
    (async () => {
      if (companyId) {
        setIsReady(false);
        await connectedPageCRUDActions.retrieveBasicListByCompany(companyId, {
          withSubscription: true,
        });
        setIsReady(true);
      }
    })();
  }, [companyId]);

  if (!isReady) {
    return (
      <Spinner
        className="invite-user-modal__spinner"
        backgroundColor="transparent"
        position="relative"
        size={24}
        show
      />
    );
  }

  return (
    <div
      className={classNames('facebook-pages-access', {
        'facebook-pages-access--disabled': disabled,
      })}
    >
      <div className="facebook-pages-access__header">
        <Heading size={5} isBold={false}>
          Pages
        </Heading>
        <div className="facebook-pages-access__actions">
          <span
            className="facebook-pages-access__action"
            onClick={partial(selectAll, true)}
          >
            Select all
          </span>
          <span
            className="facebook-pages-access__action"
            onClick={partial(selectAll, false)}
          >
            Clear
          </span>
        </div>
      </div>

      <ScrollArea className="facebook-pages-access__pages">
        {pages?.map(page => (
          <FacebookPageEntry
            disabled={disabled || page.disabled}
            page={page}
            selected={isSelected(page.id)}
            onSelect={onSelect}
            onAddSeatsClick={onAddSeats}
            onUpgradeButtonClick={onUpgrade}
            canSelectInformer={canSelectInformer}
          />
        ))}
      </ScrollArea>
    </div>
  );
};
